import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import {
  GAP_ANALYSIS_UPDATED_EVENT,
  loadSharedGapQuestionsForPatient,
} from '../utils/patientGapAnalysisStore';
import {
  loadPublishedProtocols,
  PUBLISHED_PROTOCOLS_UPDATED_EVENT,
} from '../utils/publishedProtocolsStore';
import {
  loadLastPatientCheckinAt,
  PATIENT_CHECKIN_UPDATED_EVENT,
} from '../utils/patientCheckinStore';

export interface PatientTodoItem {
  id: 'questions' | 'daily-checkin' | 'weekly-checkin' | 'monthly-checkin';
  label: string;
  helperText: string;
  path: string;
  due: boolean;
  count?: number;
}

const isSameDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const isSameMonth = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();

const getWeekStart = (value: Date): Date => {
  const day = value.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const next = new Date(value);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + diffToMonday);
  return next;
};

const isSameWeek = (left: Date, right: Date): boolean =>
  getWeekStart(left).getTime() === getWeekStart(right).getTime();

export const usePatientTodoSummary = () => {
  const { user } = useAuth();
  const [pendingQuestionCount, setPendingQuestionCount] = useState(0);
  const [hasActiveProtocol, setHasActiveProtocol] = useState(false);
  const [lastCheckinAt, setLastCheckinAt] = useState<string | undefined>(undefined);

  useEffect(() => {
    const refreshTodoData = () => {
      const pendingQuestions = loadSharedGapQuestionsForPatient(user?.id, user?.displayName).filter(
        (record) => record.question.patientStatus === 'PENDING_PATIENT'
      ).length;

      const protocols = loadPublishedProtocols();
      const normalizedName = user?.displayName.trim().toLowerCase() ?? '';
      const hasActive = protocols.some((protocol) => {
        const matchesId = user?.id ? protocol.patientId === user.id : false;
        const matchesName = protocol.patientName.trim().toLowerCase() === normalizedName;
        return (matchesId || matchesName) && protocol.status === 'ACTIVE' && Boolean(protocol.sharedWithPatient);
      });

      setPendingQuestionCount(pendingQuestions);
      setHasActiveProtocol(hasActive);
      setLastCheckinAt(loadLastPatientCheckinAt(user?.id, user?.displayName));
    };

    refreshTodoData();
    window.addEventListener('storage', refreshTodoData);
    window.addEventListener(GAP_ANALYSIS_UPDATED_EVENT, refreshTodoData);
    window.addEventListener(PATIENT_CHECKIN_UPDATED_EVENT, refreshTodoData);
    window.addEventListener(PUBLISHED_PROTOCOLS_UPDATED_EVENT, refreshTodoData);

    return () => {
      window.removeEventListener('storage', refreshTodoData);
      window.removeEventListener(GAP_ANALYSIS_UPDATED_EVENT, refreshTodoData);
      window.removeEventListener(PATIENT_CHECKIN_UPDATED_EVENT, refreshTodoData);
      window.removeEventListener(PUBLISHED_PROTOCOLS_UPDATED_EVENT, refreshTodoData);
    };
  }, [user?.displayName, user?.id]);

  const todoItems = useMemo(() => {
    const now = new Date();
    const parsedLastCheckin = lastCheckinAt ? new Date(lastCheckinAt) : undefined;
    const hasCompletedToday = parsedLastCheckin ? isSameDay(parsedLastCheckin, now) : false;
    const hasCompletedThisWeek = parsedLastCheckin ? isSameWeek(parsedLastCheckin, now) : false;
    const hasCompletedThisMonth = parsedLastCheckin ? isSameMonth(parsedLastCheckin, now) : false;

    const items: PatientTodoItem[] = [
      {
        id: 'questions',
        label: 'Questions from nutritionist',
        helperText: pendingQuestionCount > 0 ? 'Awaiting your response' : 'No pending responses',
        path: '/patient/questions-from-nutritionist',
        due: pendingQuestionCount > 0,
        count: pendingQuestionCount,
      },
      {
        id: 'daily-checkin',
        label: 'Daily check-in',
        helperText: hasCompletedToday ? 'Completed today' : 'Due today',
        path: '/patient/check-in',
        due: !hasCompletedToday,
      },
      {
        id: 'weekly-checkin',
        label: 'Weekly protocol check-in',
        helperText: hasCompletedThisWeek ? 'Completed this week' : 'Due this week',
        path: '/patient/check-in',
        due: hasActiveProtocol && !hasCompletedThisWeek,
      },
      {
        id: 'monthly-checkin',
        label: 'Monthly protocol check-in',
        helperText: hasCompletedThisMonth ? 'Completed this month' : 'Due this month',
        path: '/patient/check-in',
        due: hasActiveProtocol && !hasCompletedThisMonth,
      },
    ];

    return items;
  }, [hasActiveProtocol, lastCheckinAt, pendingQuestionCount]);

  const dueCount = todoItems.filter((item) => item.due).reduce((sum, item) => sum + (item.count ?? 1), 0);

  return {
    todoItems,
    dueCount,
  };
};
